// Libs
import classNames from 'classnames/bind';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
// Others
import { IFormQuotes } from '~/utils/interface/quotes';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { DepositMadeEnum, DiscountTypeEnum } from '~/utils/enum/common';
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './SummaryQuoteForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const SummaryQuoteForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);

  const { setValue, watch } = useFormContext<IFormQuotes>();

  const {
    taxRate,
    taxAmount,
    poNumber,
    subtotal,
    couponAmount,
    total,
    productSubtotal,
    laborCost,
    deliveryCost,
    discountRate,
    couponType,
    depositMadeAmount,
    couponRate,
    couponId,
    depositMade,
    damageWaiver,
    laborFee,
    customFees = [],
  } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const customFeesAmountDraft = customFees.reduce((sum, fee) => {
      return sum + Number(fee.customFeeAmount || 0);
    }, 0);

    const productSubtotalAfterDiscount = Number(productSubtotal || 0) - Number(discountRate || 0);
    const subtotalDraft =
      productSubtotalAfterDiscount +
      Number(deliveryCost || 0) +
      Number(damageWaiver || 0) +
      Number(laborFee || 0) +
      Number(laborCost || 0) +
      Number(customFeesAmountDraft || 0);

    const couponAmountDraft = handleCalcCouponAmount(Number(subtotalDraft));
    const appliedCouponAmount = Number(couponAmountDraft || 0);

    const subTotalAfterCoupon =
      Number(subtotalDraft - appliedCouponAmount) > 0 ? Number(subtotalDraft - appliedCouponAmount) : 0;
    const taxAmountDraft = (subTotalAfterCoupon * Number(taxRate || 0)) / 100;

    const totalDraft = subTotalAfterCoupon + taxAmountDraft;

    setValue('subtotal', subtotalDraft.toFixed(2).toString());
    setValue('couponAmount', appliedCouponAmount.toFixed(2).toString());
    setValue('taxAmount', taxAmountDraft.toFixed(2).toString());
    setValue('total', totalDraft.toFixed(2).toString());
  }, [
    productSubtotal,
    discountRate,
    deliveryCost,
    damageWaiver,
    couponId,
    couponType,
    couponRate,
    laborFee,
    laborCost,
    taxRate,
    JSON.stringify(customFees),
  ]);

  useEffect(() => {
    switch (depositMade) {
      case DepositMadeEnum.NOT_REQUIRED:
        setValue('depositMadeAmount', '0');
        break;
      case DepositMadeEnum.RENTAL_ORDER_DEPOSIT:
        setValue('depositMadeAmount', ((Number(total || 0) * 30) / 100).toFixed(2).toString());
        break;
      case DepositMadeEnum.WASHING_ORDER_DEPOSIT:
        setValue('depositMadeAmount', ((Number(total || 0) * 50) / 100).toFixed(2).toString());
        break;
      default:
        break;
    }
  }, [depositMade, total]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCalcCouponAmount = (subtotalValue: number) => {
    switch (couponType) {
      case DiscountTypeEnum.PERCENTAGE:
        return (Number(couponRate || 0) * Number(subtotalValue || 0)) / 100;
      case DiscountTypeEnum.AMOUNT:
        return Number(couponRate || 0);
      default:
        return EMPTY_STRING;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='summaryQuoteFormComponent' className={cx('container')}>
      <BaseLabel styleType='title' label={t('form_quotes_title_summary')} />

      <Box className={cx('content')}>
        <Stack gap={'13px'}>
          <Typography className={cx('subTitle')}>
            {t('form_quotes_title_subtotal', { subtotal: formatCurrencyUSD({ value: subtotal, showDecimal: true }) })}
          </Typography>

          <Typography className={cx('subTitle')}>
            {t('form_quotes_title_coupon_amount', {
              couponAmount: formatCurrencyUSD({ value: couponAmount, showDecimal: true }),
            })}
          </Typography>

          <Typography className={cx('subTitle')}>
            {t('form_quotes_title_deposit', {
              deposit: formatCurrencyUSD({ value: depositMadeAmount, showDecimal: true }),
            })}
          </Typography>

          <Typography className={cx('subTitle')}>
            {t('form_quotes_title_tax', {
              taxRate: taxRate || 0,
              taxAmount: formatCurrencyUSD({ value: taxAmount, showDecimal: true }),
            })}
          </Typography>

          <Typography className={cx('subTitle')}>
            {t('form_quotes_title_po_number', { poNumber: poNumber, showDecimal: true })}
          </Typography>
        </Stack>

        <Stack justifyContent={'flex-end'}>
          <Typography className={cx('textTotal')}>
            {t('form_quotes_title_total', { total: formatCurrencyUSD({ value: total, showDecimal: true }) })}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default SummaryQuoteForm;
